import * as React from 'react';

import classnames from 'classnames';

import type {
    GetQuoteById_quote_productLineItems_edges_node as ProductLineItem,
    GetPossibleProducts_quotePossibleProducts_edges as QuotePossibleProductsEdge,
} from 'nutshell-graphql-types';

import {Badge} from '../../badge/badge';
import {colors} from '../../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {CloseIcon} from '../../icon/svg/close-icon';
import {StackLegacy} from '../../layout/stack/stack-legacy';
import {OptionWithSubtitleAndSublabel} from '../../select-option-components/option-with-subtitle-and-sublabel';
import {SelectPickerGeneric} from '../../select-picker-generic/select-picker-generic';
import {TextField} from '../../textfield/textfield';
import {Body} from '../../typography/body';

import type {FieldError} from '../helpers';

import {FIELD_NAMES, TERM_OPTIONS, COLUMN_WIDTHS, COLUMN_WIDTHS_RESPONSIVE} from './consts';
import {
    createDiscountOptions,
    createProductDropdownOptions,
    onProductFieldChange,
    onNewProductName,
    getEditableColumnWidths,
    getDiscountUnitOption,
    getFormattedTotal,
} from './helpers';

import styles from './quote-product-table.module.css';

type Props = {
    item: ProductLineItem;
    showSku: boolean;
    showTerm: boolean;
    showDiscount: boolean;
    index: number;
    handleSave: (index: number, updatedProduct: ProductLineItem) => void;
    handleDelete: (index: number) => void;
    quotePossibleProductsEdges?: Array<QuotePossibleProductsEdge>;
    isSmallScreen: boolean;
};

export function QuoteProductTableRowEditable(props: Props) {
    const [isNewProduct, setIsNewProduct] = React.useState(props.item.id.includes('New-Item-Id'));
    const [errorMessage, setErrorMessage] = React.useState<FieldError>({
        message: '',
        field: '',
    });

    const onFieldChange = (field: string, value: string, subfield?: string) => {
        onProductFieldChange(
            field,
            value,
            subfield,
            props.item,
            props.handleSave,
            props.index,
            setErrorMessage
        );
    };

    const discountUnitOptions = createDiscountOptions(props.item.price.symbol);
    const possibleProducts = createProductDropdownOptions(props.quotePossibleProductsEdges || []);
    const editableColumnWidths = getEditableColumnWidths(
        props.isSmallScreen ? COLUMN_WIDTHS_RESPONSIVE : COLUMN_WIDTHS
    );

    return (
        <div className='pos-relative'>
            <Badge
                contents={<CloseIcon size={8} fill={colors.white} wrapWithDiv={false} />}
                onClick={() => props.handleDelete(props.index)}
                color='grey'
            />
            <div className='flex px-4'>
                <div style={{width: editableColumnWidths.name}}>
                    <div className={classnames(styles['input-column'], styles['text-left'])}>
                        <StackLegacy spacing={8}>
                            {isNewProduct ? (
                                <SelectPickerGeneric
                                    options={possibleProducts}
                                    placeholder='Select a product'
                                    value={possibleProducts.find(
                                        (option) =>
                                            option.value ===
                                            (props.item.product && props.item.product.id)
                                    )}
                                    // @ts-expect-error
                                    onChange={(option) => {
                                        onNewProductName(
                                            option.value,
                                            props.item,
                                            props.index,
                                            props.quotePossibleProductsEdges || [],
                                            props.handleSave
                                        );
                                        setIsNewProduct(false);
                                    }}
                                    minHeight={34}
                                    components={{
                                        Option: OptionWithSubtitleAndSublabel,
                                    }}
                                    // @ts-expect-error
                                    filterOption={(option, search) => {
                                        if (option.data) {
                                            return (
                                                (option.data.subtitle &&
                                                    option.data.subtitle
                                                        .toLowerCase()
                                                        .includes(search.toLowerCase())) ||
                                                (option.data.label &&
                                                    option.data.label
                                                        .toLowerCase()
                                                        .includes(search.toLowerCase())) ||
                                                (option.data.sublabel &&
                                                    option.data.sublabel
                                                        .toLowerCase()
                                                        .includes(search.toLowerCase()))
                                            );
                                        }

                                        return option.label
                                            .toLowerCase()
                                            .includes(search.toLowerCase());
                                    }}
                                />
                            ) : (
                                <TextField
                                    placeholder='Enter a product name'
                                    value={props.item.name ? props.item.name : ''}
                                    onChange={(value) => onFieldChange(FIELD_NAMES.name, value)}
                                />
                            )}
                        </StackLegacy>
                    </div>
                </div>
                <div style={{width: editableColumnWidths.sku}}>
                    <div className={classnames(styles['input-column'], styles['text-left'])}>
                        {props.showSku && (
                            <TextField
                                placeholder='SKU'
                                value={props.item.sku ? props.item.sku : ''}
                                onChange={(value) => onFieldChange(FIELD_NAMES.sku, value)}
                            />
                        )}
                    </div>
                </div>
                <div style={{width: editableColumnWidths.quantity}}>
                    <div className={styles['input-column']}>
                        <TextField
                            placeholder='Quantity'
                            value={props.item.quantity ? `${props.item.quantity}` : ''}
                            onChange={(value) => onFieldChange(FIELD_NAMES.quantity, value)}
                            hasError={errorMessage.field === FIELD_NAMES.quantity}
                        />
                    </div>
                </div>
                <div style={{width: editableColumnWidths.price}}>
                    <div className={styles['input-column']}>
                        <StackLegacy spacing={2} isHorizontal={true} alignCenter={true}>
                            <Body as='div' size='normal'>
                                {props.item.price.symbol}
                            </Body>
                            <TextField
                                placeholder='Price'
                                value={`${props.item.price.amount}`}
                                onChange={(value) =>
                                    onFieldChange(FIELD_NAMES.price, value, 'amount')
                                }
                                hasError={errorMessage.field === FIELD_NAMES.price}
                            />
                        </StackLegacy>
                    </div>
                </div>
                <div style={{width: editableColumnWidths.term}}>
                    <div className={styles['input-column']}>
                        {props.showTerm && (
                            <SelectPickerGeneric
                                options={TERM_OPTIONS}
                                placeholder='Term'
                                value={TERM_OPTIONS.find(
                                    (option) => option.value === props.item.term
                                )}
                                // @ts-expect-error
                                onChange={(option) => {
                                    onFieldChange(FIELD_NAMES.term, option.value);
                                }}
                                minHeight={34}
                                styles={{
                                    valueContainer: () => ({
                                        padding: '0 4px',
                                    }),
                                    dropdownIndicator: () => ({
                                        paddingLeft: '6px',
                                        paddingRight: '2px',
                                    }),
                                }}
                            />
                        )}
                    </div>
                </div>
                <div style={{width: editableColumnWidths.discount}}>
                    <div className={styles['input-column']}>
                        {props.showDiscount && (
                            <StackLegacy spacing={2} isHorizontal={true} alignCenter={true}>
                                <div className={styles['discount-input']}>
                                    <TextField
                                        placeholder=''
                                        value={
                                            props.item.discountNumber
                                                ? `${props.item.discountNumber}`
                                                : ''
                                        }
                                        onChange={(value) => {
                                            onFieldChange(FIELD_NAMES.discountNumber, value);
                                        }}
                                        hasError={errorMessage.field === FIELD_NAMES.discountNumber}
                                    />
                                </div>
                                <SelectPickerGeneric
                                    options={discountUnitOptions}
                                    isSearchable={false}
                                    placeholder=''
                                    value={getDiscountUnitOption(
                                        discountUnitOptions,
                                        props.item.discountUnit
                                    )}
                                    // @ts-expect-error
                                    onChange={(option) => {
                                        onFieldChange(FIELD_NAMES.discountUnit, option.value);
                                    }}
                                    minHeight={34}
                                    styles={{
                                        valueContainer: () => ({
                                            padding: '0 4px',
                                        }),
                                        dropdownIndicator: () => ({
                                            paddingLeft: '6px',
                                            paddingRight: '2px',
                                        }),
                                    }}
                                />
                            </StackLegacy>
                        )}
                    </div>
                </div>
                <div style={{width: editableColumnWidths.total}}>
                    <div className={styles['input-column']}>
                        <StackLegacy spacing={2} isHorizontal={true} alignCenter={true}>
                            <TextField
                                placeholder='Total'
                                value={getFormattedTotal(props.item)}
                                // @ts-expect-error
                                disabled={true}
                            />
                        </StackLegacy>
                    </div>
                </div>
            </div>
            {errorMessage && (
                <Body color='error' size='small'>
                    {errorMessage.message}
                </Body>
            )}
        </div>
    );
}
